import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Mail, Users, Send, Plus, LogOut } from 'lucide-react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

interface InvitationCode {
  id: string;
  email: string;
  code: string;
  status: 'pending' | 'sent' | 'used';
  created_at: string;
  sent_at?: string;
  used_at?: string;
  used_by_user_id?: string;
}

interface GenerateInvitationsResponse {
  success: boolean;
  invitations: InvitationCode[];
  message?: string;
  error?: string;
}

interface SendInvitationEmailsResponse {
  success: boolean;
  sentCount: number;
  message?: string;
  error?: string;
}

export const InvitationManager: React.FC = () => {
  const [emails, setEmails] = useState('');
  const [invitations, setInvitations] = useState<InvitationCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingInvitations, setFetchingInvitations] = useState(false);

  const { authenticatedFetch, logout } = useAdminAuth();
  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

  const loadInvitations = async () => {
    setFetchingInvitations(true);
    try {
      const response = await authenticatedFetch(`${backendUrl}/api/admin/invitations/list`);
      const data = await response.json();

      if (data.success) {
        setInvitations(data.invitations || []);
      } else {
        toast.error('Failed to load invitations');
      }
    } catch (error) {
      console.error('Failed to load invitations:', error);
      toast.error('Failed to load invitations');
    } finally {
      setFetchingInvitations(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  const handleGenerate = async () => {
    if (!emails.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }

    setLoading(true);
    try {
      const emailList = emails.split('\n')
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));

      if (emailList.length === 0) {
        toast.error('No valid email addresses found');
        return;
      }

      const response = await authenticatedFetch(`${backendUrl}/api/admin/invitations/generate`, {
        method: 'POST',
        body: JSON.stringify({ emails: emailList })
      });

      const result: GenerateInvitationsResponse = await response.json();
      
      if (result.success) {
        toast.success(result.message || `Successfully generated ${result.invitations.length} invitation codes`);
        setEmails('');
        loadInvitations();
      } else {
        toast.error(result.error || 'Failed to generate invitation codes');
      }
    } catch (error) {
      console.error('Failed to generate invitations:', error);
      toast.error('Failed to generate invitation codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmails = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`${backendUrl}/api/admin/invitations/send-emails`, {
        method: 'POST'
      });

      const result: SendInvitationEmailsResponse = await response.json();
      
      if (result.success) {
        toast.success(result.message || `Successfully sent ${result.sentCount} invitation emails`);
        loadInvitations();
      } else {
        toast.error(result.error || 'Failed to send invitation emails');
      }
    } catch (error) {
      console.error('Failed to send emails:', error);
      toast.error('Failed to send invitation emails');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'sent':
        return <Badge variant="default">Sent</Badge>;
      case 'used':
        return <Badge variant="outline">Used</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const pendingCount = invitations.filter(inv => inv.status === 'pending').length;
  const sentCount = invitations.filter(inv => inv.status === 'sent').length;
  const usedCount = invitations.filter(inv => inv.status === 'used').length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Invitation Code Manager</h1>
        <div className="flex gap-2">
          <Button onClick={loadInvitations} disabled={fetchingInvitations}>
            {fetchingInvitations ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : null}
            Refresh
          </Button>
          <Button variant="outline" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{invitations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Sent</p>
                <p className="text-2xl font-bold">{sentCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Used</p>
                <p className="text-2xl font-bold">{usedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Generate Invitations */}
      <Card>
        <CardHeader>
          <CardTitle>Generate Invitation Codes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="emails">Email Addresses</Label>
            <textarea
              id="emails"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="Enter email addresses, one per line:&#10;user1@example.com&#10;user2@example.com&#10;user3@example.com"
              rows={8}
              className="w-full p-3 border rounded-md resize-none font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Enter one email address per line. Duplicate emails will be skipped.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={handleGenerate} disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Generate Invitation Codes
            </Button>
            
            <Button 
              onClick={handleSendEmails} 
              disabled={loading || pendingCount === 0}
              variant="outline"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Pending Emails ({pendingCount})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invitations List */}
      <Card>
        <CardHeader>
          <CardTitle>Invitation Codes</CardTitle>
        </CardHeader>
        <CardContent>
          {fetchingInvitations ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading invitations...
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No invitation codes found. Generate some codes to get started.
            </div>
          ) : (
            <div className="space-y-2">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="font-mono text-lg font-bold">
                      {invitation.code}
                    </div>
                    <div>
                      <p className="font-medium">{invitation.email}</p>
                      <p className="text-sm text-muted-foreground">
                        Created: {formatDate(invitation.created_at)}
                      </p>
                      {invitation.sent_at && (
                        <p className="text-sm text-muted-foreground">
                          Sent: {formatDate(invitation.sent_at)}
                        </p>
                      )}
                      {invitation.used_at && (
                        <p className="text-sm text-muted-foreground">
                          Used: {formatDate(invitation.used_at)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    {getStatusBadge(invitation.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
